import { signOut } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { AnimatePresence, motion } from "framer-motion";
import {
	BarChart,
	BarChart3,
	HelpCircle,
	LogOut,
	Menu,
	MessageSquare,
	Settings as SettingsIcon,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";
import { Tooltip } from "react-tooltip";
import Auth from "./components/Auth";
import Dashboard from "./components/Dashboard";
import FAQ from "./components/FAQ";
import Settings from "./components/Settings";
import Therapist from "./components/Therapist";
import TherapyHome from "./components/TherapyHome";
import AdminDashboardServerSide from "./components/AdminDashboardServerSide";
import AdminGuard from "./components/AdminGuard";
import { useUser } from "./contexts/UserContext";
import { auth, db } from "./firebaseConfig";

const App = () => {
	const { user, setUser, loading, isAdmin } = useUser();
	const navigate = useNavigate();
	const location = useLocation();
	const [isNavOpen, setIsNavOpen] = useState(false);
	const [termsAccepted, setTermsAccepted] = useState(true);

	useEffect(() => {
		const checkTerms = async () => {
			if (user) {
				const userDoc = await getDoc(doc(db, "Users", user.uid));
				setTermsAccepted(userDoc.data()?.termsSigned || false);
			}
		};
		checkTerms();
	}, [user]);

	const handleAcceptTerms = async () => {
		try {
			await updateDoc(doc(db, "Users", user.uid), {
				termsSigned: true,
			});
			setTermsAccepted(true);
		} catch (error) {
			console.error("Error updating terms acceptance:", error);
		}
	};

	const handleNavItemClick = (path) => {
		navigate(path);
		setIsNavOpen(false);
	};

	const handleLogout = async () => {
		try {
			await signOut(auth);
			setUser(null);
		} catch (error) {
			console.error("Error signing out:", error);
		}
	};

	const NavBar = () => (
		<>
			{/* Mobile Menu Button */}
			<AnimatePresence>
				{!isNavOpen && (
					<motion.button
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="md:hidden fixed top-2 left-2 z-50 p-2 rounded-md bg-transparent"
						onClick={() => setIsNavOpen(!isNavOpen)}>
						<Menu size={24} />
					</motion.button>
				)}
			</AnimatePresence>

			{/* Desktop Navigation - Always visible on md and up */}
			<div className="hidden md:flex w-24 h-screen bg-gray-100 text-gray-800 p-4 border-r border-gray-200 shadow-md flex-col">
				<div className="w-full">
					<button
						data-tooltip-id="nav-tooltip"
						data-tooltip-content="Logout"
						className="w-full flex items-center p-2 text-red-600 hover:bg-red-100 rounded justify-center h-10"
						onClick={handleLogout}>
						<LogOut size={16} />
					</button>
				</div>
				<nav className="flex-1 flex items-center">
					<ul className="space-y-4 w-full">
						<li
							data-tooltip-id="nav-tooltip"
							data-tooltip-content="Dashboard"
							onClick={() => handleNavItemClick("/dashboard")}
							className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
								location.pathname.startsWith("/dashboard")
									? "bg-gray-300"
									: ""
							}`}>
							<BarChart size={16} />
						</li>
						<li
							data-tooltip-id="nav-tooltip"
							data-tooltip-content="Talk"
							onClick={() => handleNavItemClick("/therapy-home")}
							className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
								location.pathname.startsWith("/therapy")
									? "bg-gray-300"
									: ""
							}`}>
							<MessageSquare size={16} />
						</li>
						<li
							data-tooltip-id="nav-tooltip"
							data-tooltip-content="FAQ"
							onClick={() => handleNavItemClick("/faq")}
							className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
								location.pathname.startsWith("/faq")
									? "bg-gray-300"
									: ""
							}`}>
							<HelpCircle size={19} />
						</li>
						{isAdmin && (
							<li
								data-tooltip-id="nav-tooltip"
								data-tooltip-content="Admin"
								onClick={() => handleNavItemClick("/admin")}
								className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
									location.pathname.startsWith("/admin")
										? "bg-gray-300"
										: ""
								}`}>
								<BarChart3 size={16} />
							</li>
						)}
					</ul>
				</nav>
				<li
					data-tooltip-id="nav-tooltip"
					data-tooltip-content="Settings"
					onClick={() => handleNavItemClick("/settings")}
					className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center hover:bg-gray-200 ${
						location.pathname.startsWith("/settings")
							? "bg-gray-300"
							: ""
					}`}>
					<SettingsIcon size={16} />
				</li>
				<Tooltip id="nav-tooltip" place="right" />
			</div>

			{/* Mobile Navigation */}
			<AnimatePresence>
				{isNavOpen && (
					<motion.div
						initial={{ x: "-100%" }}
						animate={{ x: 0 }}
						exit={{ x: "-100%" }}
						transition={{
							type: "spring",
							bounce: 0.1,
							duration: 0.3,
						}}
						className="fixed md:hidden w-64 h-screen bg-gray-100 text-gray-800 p-4 border-r border-gray-200 shadow-md flex flex-col z-40">
						<nav className="flex-1 flex items-center">
							<ul className="space-y-4 w-full">
								<li
									onClick={() =>
										handleNavItemClick("/dashboard")
									}
									className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
										location.pathname.startsWith(
											"/dashboard"
										)
											? "bg-gray-300"
											: ""
									}`}>
									<BarChart size={16} />
									Dashboard
								</li>
								<li
									onClick={() =>
										handleNavItemClick("/therapy-home")
									}
									className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
										location.pathname.startsWith("/therapy")
											? "bg-gray-300"
											: ""
									}`}>
									<MessageSquare size={16} />
									Talk
								</li>
								<li
									onClick={() => handleNavItemClick("/faq")}
									className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
										location.pathname.startsWith("/faq")
											? "bg-gray-300"
											: ""
									}`}>
									<HelpCircle size={19} />
									FAQ
								</li>
								{isAdmin && (
									<li
										onClick={() => handleNavItemClick("/admin")}
										className={`p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 ${
											location.pathname.startsWith("/admin")
												? "bg-gray-300"
												: ""
										}`}>
										<BarChart3 size={16} />
										Admin
									</li>
								)}
								<li
									onClick={() =>
										handleNavItemClick("/settings")
									}
									className="p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200">
									<SettingsIcon size={16} />
									Settings
								</li>
								<li
									className="p-2 rounded cursor-pointer flex items-center h-10 justify-center gap-2 hover:bg-gray-200 text-red-600 hover:bg-red-200"
									onClick={handleLogout}>
									<LogOut size={16} />
									Logout
								</li>
							</ul>
						</nav>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Overlay */}
			<AnimatePresence>
				{isNavOpen && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 0.5 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className="fixed inset-0 bg-black md:hidden z-30"
						onClick={() => setIsNavOpen(false)}
					/>
				)}
			</AnimatePresence>
		</>
	);

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
			</div>
		);
	}

	// Show auth if no user
	if (!user) {
		return <Auth />;
	}

	// Show main app if authenticated and has subscription
	return (
		<div className="flex">
			<NavBar />
			<div className="flex-1">
				{!termsAccepted ? (
					<div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
						<div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl transform transition-all">
							<h2 className="text-2xl font-bold mb-6 text-gray-800">
								Welcome to HeyAkua!
							</h2>
							<div className="space-y-6 mb-8">
								<p className="text-gray-600 font-medium">
									Before we begin, please understand that:
								</p>
								<ul className="space-y-4">
									<li className="flex items-start">
										<span className="inline-block w-2 h-2 mt-2 mr-3 bg-blue-500 rounded-full"></span>
										<p className="text-gray-700 flex-1">
											Akua is designed to sound, act, and
											respond like a human therapist, but
											she is an AI model, not a licensed
											professional.
										</p>
									</li>
									<li className="flex items-start">
										<span className="inline-block w-2 h-2 mt-2 mr-3 bg-blue-500 rounded-full"></span>
										<p className="text-gray-700 flex-1">
											If you are facing serious mental
											health challenges, please seek help
											from a qualified professional. Akua
											is not a substitute for professional
											care, especially for severe mental
											health conditions.
										</p>
									</li>
								</ul>
							</div>
							<button
								onClick={handleAcceptTerms}
								className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium text-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
								I Understand
							</button>
						</div>
					</div>
				) : (
					<Routes>
						<Route path="/therapy" element={<Therapist />} />
						<Route path="/settings" element={<Settings />} />
						<Route path="/dashboard" element={<Dashboard />} />
						<Route 
							path="/admin" 
							element={
								<AdminGuard isAdmin={isAdmin}>
									<AdminDashboardServerSide />
								</AdminGuard>
							} 
						/>
						<Route path="/faq" element={<FAQ />} />
						<Route path="/therapy-home" element={<TherapyHome />} />
						<Route
							path="*"
							element={<Navigate to="/dashboard" />}
						/>
					</Routes>
				)}
			</div>
		</div>
	);
};

export default App;
